<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <div id="shopLaunch">
    <component :is="'style'" v-html="googleFontsCss"></component>
    <div id="gfContainer" class="gfContainer-loading">
      <VisualFrame />
      <Navbar @toggleEditPageTitleModal="toggleEditPageTitleModal" />
      <NotificationBar />
      <EditorSidebar />
      <PlanModal />
      <GFrame />
      <Guide />
      <div class="side-tooltips"></div>
    </div>
    <GLoader />
    <div id="divContentHidden">
      <div id="md-back"></div>
      <Library />
      <PopupResident />
    </div>
    <div id="md-unknown" class="md-modal md-draggable">
      <div class="md-content">
        <div class="md-modal-handle">
          <span class="md-title"><span></span>Unknown Block</span>
        </div>
        <div class="md-body">
          <p>
            Unknown selected block. It might be because this block has been upgraded. We recommend to remove it and drag
            & drop a same element from Left Sidebar again. Thank you!
          </p>
        </div>
      </div>
      <div class="md-controls">
        <button class="btnClose"><i class="fa fa-times" aria-hidden="true"></i></button>
      </div>
    </div>
    <GLoading v-if="gLoading.active" class="go-global" size="large">
      <template #text>{{ gLoading.text }}</template>
    </GLoading>

    <div id="modals" teleport></div>
    <div id="tooltips" teleport></div>

    <MaintainModal v-if="shopInfo" />
    <GTooltipsNew />
    <OverlayMinResize />
    <AppOverlayFullScreen v-show="showOverlayApp" />
    <GetSourceModal v-if="showGetSourceModal" />
    <FontManagerModal v-if="showFontManager" />
    <EditPageTitleModal v-if="editPageTitle.visible" @close="toggleEditPageTitleModal" />
    <ShareLibraryOnboarding v-if="showShareLibraryOnBoarding" />
    <SaveToLibraryOnBoarding v-if="showAddLibraryOnBoarding?.visible" />
    <GemMeter v-if="isLandingPage()" />
  </div>
</template>

<script lang="ts">
import '@/app/editor/index.ts';

import { defineComponent, defineAsyncComponent } from 'vue';
import type { TypeShop } from '@/app/types/editor/shop';
import type { TypePage } from './store/app/types';
import store from '@/app/store/store';
import * as Sentry from '@sentry/vue';
import { mapMutations, mapState, mapActions, mapGetters } from 'vuex';
import { actions as commonActions } from '@/store/common/types';
import PopupResident from '@/core/components/popup/PopupResident.vue';
import { mutations as AppMutations } from './store/app/types';
import { EDITOR_EVENT } from '@/app/editor/config/constants/trackEventIds';

import VisualFrame from '@/views/VisualFrame.vue';
import Navbar from '@/views/Navbar/index.vue';
import NotificationBar from '@/views/NotificationBar/index.vue';
import { EditorSidebar, PlanModal } from '@modules/editor-sidebar';
import GFrame from '@/views/GFrame/index.vue';
import Guide from '@/views/Guide/index.vue';
import GLoader from '@/views/GLoader/index.vue';
import OverlayMinResize from '@/views/OverlayMinResize.vue';
import GTooltipsNew from '@/views/GTooltipsNew.vue';
import { app } from '@/store';
import { ShareLibraryOnboarding } from '@modules/libraries';

const EditPageTitleModal = defineAsyncComponent(() => import('@/views/Navbar/EditPageTitleModal.vue'));
const AppOverlayFullScreen = defineAsyncComponent(() => import('@/views/AppOverlayFullScreen.vue'));
const FontManagerModal = defineAsyncComponent(() => import('@/views/Navbar/FontManagerModal/index.vue'));
const GetSourceModal = defineAsyncComponent(() => import('@/views/GetSource/index.vue'));
const MaintainModal = defineAsyncComponent(() => import('@/views/MaintainModal/index.vue'));
const Library = defineAsyncComponent(() => import('@modules/libraries'));
const SaveToLibraryOnBoarding = defineAsyncComponent(
  () => import('@modules/libraries/components/tabs/saved/SaveToLibraryOnBoarding.vue'),
);
import Api from './services/Api';
import GemMeter, { isLandingPage } from '@/modules/gem-meter';
import config from '@/app/editor/config';

export default defineComponent({
  name: 'App',
  components: {
    Navbar,
    OverlayMinResize,
    EditorSidebar,
    GFrame,
    GLoader,
    Guide,
    NotificationBar,
    Library,
    VisualFrame,
    GTooltipsNew,
    PopupResident,
    GetSourceModal,
    MaintainModal,
    PlanModal,
    FontManagerModal,
    AppOverlayFullScreen,
    EditPageTitleModal,
    GemMeter,
    SaveToLibraryOnBoarding,
    ShareLibraryOnboarding,
  },
  data: (): {
    socketInterval: any;
    editPageTitle: any;
  } => ({
    socketInterval: -1,
    editPageTitle: {
      visible: false,
    },
  }),
  computed: {
    ...mapState('app', {
      pageInfo: (state: any): TypePage => state.pageInfo,
      shopInfo: (state: any): TypeShop => state.shopInfo,
      showGetSourceModal: (state: any): string => state.showGetSourceModal,
      showOverlayApp: (state: any): string => state.showOverlayApp,
    }),
    ...mapState('common', {
      gLoading: 'gloading',
    }),
    ...mapState('library', {
      showAddLibraryOnBoarding: 'showAddLibraryOnBoarding',
      showShareLibraryOnBoarding: 'showShareLibraryOnBoarding',
    }),
    ...mapGetters('font', {
      showFontManager: 'showFontManager',
      googleFontsCss: 'googleFontsCss',
    }),
  },
  watch: {
    shopInfo(newValue) {
      if (!newValue.passwordEnabled) return;
      if (!newValue.password) {
        app.$store.state['app'].enablePassword = true;
        return;
      }

      const data = {
        shop: newValue.shopifyDomain,
        password: newValue.password,
      };

      Api.base
        .post('/api/shop/checkPassword.html', data)
        .then((rs) => {
          app.$store.state['app'].enablePassword = false;
        })
        .catch((err) => {
          app.$store.state['app'].enablePassword = true;
        });
    },
  },
  mounted() {
    this.trackViewedEvent();
    const shopInStore = store.getValue('__shop');
    const pageInStore = store.getValue('__page');
    if (shopInStore) {
      this.commitShop(shopInStore);
      // if (!config.isLocal()) {
      //   Sentry.setUser({
      //     id: shopInStore?.shopifyDomain,
      //   });
      // }
    } else {
      store.onChange('__shop', (value) => {
        this.commitShop(value);
        // if (!config.isLocal()) {
        //   Sentry.setUser({
        //     id: value?.shopifyDomain,
        //   });
        // }
      });
    }

    if (pageInStore) {
      this.commitPage(pageInStore);
    } else {
      store.onChange('__page', (value) => {
        this.commitPage(value);
      });
    }

    store.onChange('__isSaved', (value) => {
      this.commitSaved(value);
    });

    store.onChange('__isMakeDefault', (value) => {
      this.commitMakeDefault(value);
    });

    this.socketInterval = setInterval(() => {
      if (window._webSocket && window._webSocket.connection.readyState === 1) {
        window._webSocket.onmessage('data_sync', (data: any) => {
          // eslint-disable-next-line camelcase
          if (this.pageInfo?.page?.g_id == data.pageID && this.pageInfo?.page?.type == data.pageType) {
            const newPageInfo = JSON.parse(JSON.stringify(this.pageInfo));
            newPageInfo.page.events = data.events;
            this.commitUpdatePageEvent(data.events);
          }
        });
        clearInterval(this.socketInterval);
      }
    }, 10);
  },
  methods: {
    ...mapActions('common', {
      trackEvent: commonActions.TRACK_EVENT,
    }),

    ...mapMutations('app', {
      commitPage: AppMutations.MUTATE_PAGE_INFO,
      commitShop: AppMutations.MUTATE_SHOP_INFO,
      commitSaved: AppMutations.MUTATE_STATE_SAVED,
      commitMakeDefault: AppMutations.MUTATE_STATE_MAKE_DEFAULT,
      commitUpdatePageEvent: AppMutations.UPDATE_PAGE_EVENTS,
    }),
    isLandingPage,
    trackViewedEvent() {
      if (window.___gemShopInfo) {
        this.trackEvent({ eventId: EDITOR_EVENT.VIEWED });
      } else {
        setTimeout(this.trackViewedEvent, 300);
      }
    },
    toggleEditPageTitleModal() {
      this.editPageTitle.visible = !this.editPageTitle.visible;
    },
  },
});
</script>

<style lang="scss">
@import '@/core/themes/grid/grid-system.scss';
@import '@/core/themes/base.scss';
@import '@/app/editor/scss/gryffEditor.scss';

#shopLaunch > .gfLoader.go-global {
  background: inherit;
  z-index: $modalIndex + 1 !important;
  background: rgba($text-light-high, 0.9) !important;

  .gfLoader-text {
    margin-top: 150px;
  }
}
</style>
